import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TbCalendarCheck, TbMessageCircle } from "react-icons/tb";
import Modal from "../Modal/Modal";
import {
  DESTINATIONS,
  resolveContextFromPath,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";
import styles from "./TrialProductModal.module.css";

const TITLE_ID = "trial-product-modal-title";

/**
 * Os dois produtos, na ordem em que aparecem. `Agendamento` vem primeiro por ser
 * o carro-chefe (é o que as 5 páginas de segmento vendem) e o default do backend.
 */
const OPTIONS = [
  {
    product: TRIAL_PRODUCTS.AGENDAMENTO,
    Icon: TbCalendarCheck,
    label: "Chatbot de Agendamento",
    pitch: "Seu cliente marca, remarca e cancela sozinho pelo WhatsApp.",
    detail: "Inclui tudo do Atendimento, mais a agenda.",
  },
  {
    product: TRIAL_PRODUCTS.ATENDIMENTO,
    Icon: TbMessageCircle,
    label: "Chatbot de Atendimento",
    pitch: "Responde dúvidas, informa preços e capta leads 24 horas.",
    detail: "Sem agenda — para quem não trabalha com hora marcada.",
  },
];

/**
 * Escolha do produto antes do cadastro, aberta pelos CTAs da home genérica.
 *
 * ── Por que existe ────────────────────────────────────────────────────────────
 * A home vende a empresa, não um produto: quem clica ali ainda não disse se quer
 * agendar ou só atender. O trial de cada um entrega um PLANO diferente no backend
 * (`FREE` × `FREE_ATENDIMENTO`), então mandar essa pessoa direto ao cadastro
 * obrigaria o servidor a chutar — e o chute erraria metade das vezes, em silêncio.
 * As páginas `/agendamento` e `/atendimento` NÃO abrem este modal: quem chegou
 * por elas já escolheu, e perguntar de novo seria atrito puro.
 *
 * ── Medição: um clique = um evento ────────────────────────────────────────────
 * O `trial_cta_click` dispara AQUI, na escolha do card — não no CTA que abriu o
 * modal. O evento significa "quer testar o produto X"; no clique anterior o X
 * ainda não existe. Disparar nos dois pontos dobraria o topo do funil e faria a
 * taxa de conversão despencar por artefato de medição, não por comportamento.
 * O `ctaPlacement` do CTA de origem viaja por prop justamente para não se perder
 * nessa troca — sem ele, todo cadastro vindo da home pareceria ter saído do mesmo
 * lugar da página.
 *
 * INV-F3: a altura limitada, o miolo rolável e a trava de scroll do `<body>` vêm
 * do {@link Modal} base — este componente não reimplementa nada disso.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} ctaPlacement - `CTA_PLACEMENTS.*` do CTA que abriu o modal.
 */
export default function TrialProductModal({ isOpen, onClose, ctaPlacement }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleChoose = useCallback(
    (product) => {
      const destination = buildTrialSignupPath(product);

      trackTrialCtaClick({
        ...resolveContextFromPath(location.pathname),
        cta_placement: ctaPlacement,
        // `destination` é a PÁGINA de destino, não a URL literal: o produto
        // viaja na dimensão própria. Assim o enum fechado de destinos não
        // precisa crescer a cada variação de query string.
        destination: DESTINATIONS.LOGIN_SIGNUP,
        product,
      });

      navigate(destination);
    },
    [ctaPlacement, location.pathname, navigate],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="620px" titleId={TITLE_ID}>
      <div className={styles.header}>
        <h2 id={TITLE_ID} className={styles.title}>
          Qual chatbot você quer testar?
        </h2>
        <p className={styles.subtitle}>
          14 dias grátis, sem cartão. Dá pra trocar depois.
        </p>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Fechar"
        >
          &times;
        </button>
      </div>

      <div className={styles.body}>
        {OPTIONS.map(({ product, Icon, label, pitch, detail }) => (
          <button
            key={product}
            type="button"
            className={styles.card}
            onClick={() => handleChoose(product)}
          >
            <span className={styles.cardIcon} aria-hidden="true">
              <Icon />
            </span>
            <span className={styles.cardLabel}>{label}</span>
            <span className={styles.cardPitch}>{pitch}</span>
            <span className={styles.cardDetail}>{detail}</span>
            <span className={styles.cardCta} aria-hidden="true">
              Testar grátis →
            </span>
          </button>
        ))}
      </div>

      <p className={styles.footnote}>
        Na dúvida? Comece pelo Agendamento — ele já faz tudo que o Atendimento faz.
      </p>
    </Modal>
  );
}
